import { useTranslation } from 'react-i18next';
import { AgentTools, type ToolCall, type ToolCallSummary } from '@/features/types/tools';
import { ToolCallContainer } from '../../ToolCallContainer';
import { ToolCallContentBox } from '../../ToolCallContentBox';
import { useToolCallParser, ToolCallState } from '../../useToolCallParser';
import { type ParsedSuccess } from '../../tool-call-utils';
import { ToolCallError } from '../../ToolCallError';

interface SoqlQueryTool {
  name: AgentTools.sf_soql_query;
  args: { query: string; allRecords?: boolean };
}

export type SoqlQueryToolCall = ToolCall<SoqlQueryTool['name'], SoqlQueryTool['args']>;

export type SoqlQueryToolCallSummary = ToolCallSummary<
  SoqlQueryTool['name'],
  SoqlQueryTool['args']
>;

interface SoqlResult {
  recordCount?: number;
  totalSize?: number;
  records: Record<string, unknown>[];
  done: boolean;
}

export function SoqlToolCall({ toolCall }: { toolCall: SoqlQueryToolCallSummary }) {
  const { t } = useTranslation('prince');

  const { parsedResult, title } = useToolCallParser<SoqlResult>(
    toolCall,
    ({ toolCallState, parsedResult }) => {
      switch (toolCallState) {
        case ToolCallState.Running:
          return t('toolCalls.sf_soql_query.title.whileRunning');
        case ToolCallState.Error:
          return t('toolCalls.sf_soql_query.title.errors');
        case ToolCallState.Success: {
          const successParsedResult = parsedResult as ParsedSuccess<SoqlResult>;
          const results = successParsedResult.results;
          const recordCount = results.recordCount || results.totalSize;
          const recordsLength = Array.isArray((results as { records?: unknown[] }).records)
            ? (results as { records?: unknown[] }).records!.length
            : 0;
          return t('toolCalls.sf_soql_query.title.success', {
            count: recordCount ?? recordsLength,
          });
        }
      }
    }
  );

  const tabs = [
    {
      id: 'results',
      label: t('toolCalls.sf_soql_query.queryResults'),
      showLoadingWhileRunning: true,
      content:
        !toolCall.isRunning && parsedResult ? (
          parsedResult.hasErrors ? (
            <ToolCallError parsedResult={parsedResult} />
          ) : (
            <ToolCallContentBox content={toolCall.results} />
          )
        ) : null,
    },
    {
      id: 'query',
      label: t('toolCalls.sf_soql_query.query'),
      content: <ToolCallContentBox content={toolCall.args.query} />,
    },
  ];

  return (
    <ToolCallContainer
      toolName={toolCall.name}
      title={title}
      isRunning={toolCall.isRunning}
      tabs={tabs}
    />
  );
}
